import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import {
  fetchCalendarEvents,
  setView,
  setSelectedDate,
  setFilters,
} from '../store/slices/calendarSlice'
import type { CalendarEventItem, CalendarEventStatus } from '@/types'
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { zhTW } from 'date-fns/locale'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

const statusStyles: Record<CalendarEventStatus, string> = {
  PLANNED: 'bg-gray-200 text-gray-700',
  CONFIRMED: 'bg-blue-200 text-blue-700',
  IN_PROGRESS: 'bg-yellow-200 text-yellow-700',
  COMPLETED: 'bg-green-200 text-green-700',
  CANCELLED: 'bg-red-200 text-red-700',
  OVERDUE: 'bg-orange-200 text-orange-700',
}

const sourceLabels: Record<string, string> = {
  'form-instance': '表單任務',
  'schedule-rule': '排程規則',
  manual: '手動事件',
}

const getDateRange = (view: 'month' | 'week' | 'day', date: Date) => {
  switch (view) {
    case 'week':
      return {
        start: startOfWeek(date, { weekStartsOn: 1 }),
        end: endOfWeek(date, { weekStartsOn: 1 }),
      }
    case 'day':
      return { start: date, end: date }
    case 'month':
    default:
      return {
        start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }),
      }
  }
}

const nextDate = (view: 'month' | 'week' | 'day', date: Date) => {
  switch (view) {
    case 'week':
      return addWeeks(date, 1)
    case 'day':
      return addDays(date, 1)
    case 'month':
    default:
      return addMonths(date, 1)
  }
}

const prevDate = (view: 'month' | 'week' | 'day', date: Date) => {
  switch (view) {
    case 'week':
      return addWeeks(date, -1)
    case 'day':
      return addDays(date, -1)
    case 'month':
    default:
      return addMonths(date, -1)
  }
}

const CalendarSchedulerPage = () => {
  const dispatch = useAppDispatch()
  const {
    eventsById,
    eventIds,
    loading,
    error,
    currentView,
    selectedDate,
    filters,
  } = useAppSelector((state) => state.calendar)

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)

  const focusDate = useMemo(() => parseISO(selectedDate), [selectedDate])
  const { start, end } = useMemo(() => getDateRange(currentView, focusDate), [currentView, focusDate])

  useEffect(() => {
    dispatch(
      fetchCalendarEvents({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        sources: filters.sources.length > 0 ? filters.sources : undefined,
      }),
    )
  }, [dispatch, start.getTime(), end.getTime(), filters.sources])

  const events = useMemo(() => eventIds.map((id) => eventsById[id]), [eventIds, eventsById])

  const selectedEvent = selectedEventId ? eventsById[selectedEventId] : undefined

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (filters.statuses.length > 0 && !filters.statuses.includes(event.status)) {
        return false
      }
      if (filters.sources.length > 0 && !filters.sources.includes(event.source)) {
        return false
      }
      return true
    })
  }, [events, filters.statuses, filters.sources])

  const eventsByDate = useMemo(() => {
    return filteredEvents.reduce<Record<string, CalendarEventItem[]>>((acc, event) => {
      const dateKey = event.startTime.slice(0, 10)
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }
      acc[dateKey].push(event)
      return acc
    }, {})
  }, [filteredEvents])

  const daysInRange = useMemo(() => eachDayOfInterval({ start, end }), [start, end])

  const handleNavigate = (direction: 'prev' | 'next') => {
    const next = direction === 'prev' ? prevDate(currentView, focusDate) : nextDate(currentView, focusDate)
    dispatch(setSelectedDate(next.toISOString()))
  }

  const handleToday = () => {
    dispatch(setSelectedDate(new Date().toISOString()))
  }

  const handleToggleStatus = (status: CalendarEventStatus) => {
    const nextStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((item) => item !== status)
      : [...filters.statuses, status]
    dispatch(setFilters({ statuses: nextStatuses }))
  }

  const handleToggleSource = (source: string) => {
    const nextSources = filters.sources.includes(source)
      ? filters.sources.filter((item) => item !== source)
      : [...filters.sources, source]
    dispatch(setFilters({ sources: nextSources }))
  }

  const renderEvents = (date: Date) => {
    const key = date.toISOString().slice(0, 10)
    const dayEvents = (eventsByDate[key] || []).sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

    if (dayEvents.length === 0) {
      return null
    }

    return (
      <ul className="mt-1 space-y-1">
        {dayEvents.map((event) => (
          <li key={event.id}>
            <button
              type="button"
              onClick={() => setSelectedEventId(event.id)}
              className={`w-full rounded-md px-2 py-1 text-left text-xs transition-colors ${
                statusStyles[event.status]
              } hover:ring-2 hover:ring-primary-400`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{event.title}</span>
                <span className="ml-2 text-[10px] opacity-80">
                  {format(new Date(event.startTime), currentView === 'day' ? 'HH:mm' : 'MM/dd')}
                </span>
              </div>
              <div className="text-[10px] opacity-80">
                {sourceLabels[event.source] || event.source}
              </div>
            </button>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => handleNavigate('prev')}
              className="rounded-md border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
              title="上一個"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              今天
            </button>
            <button
              type="button"
              onClick={() => handleNavigate('next')}
              className="rounded-md border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
              title="下一個"
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="text-lg font-semibold text-gray-900">
            {format(focusDate, currentView === 'day' ? 'yyyy年MM月dd日 (EEEE)' : 'yyyy年MM月', {
              locale: zhTW,
            })}
          </div>

          <div className="ml-auto flex items-center space-x-2">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              {[
                { label: '月', value: 'month' as const },
                { label: '週', value: 'week' as const },
                { label: '日', value: 'day' as const },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => dispatch(setView(option.value))}
                  className={`border border-gray-300 px-3 py-1.5 text-sm font-medium first:rounded-l-md last:rounded-r-md ${
                    currentView === option.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <section className="border-b border-gray-200 bg-gray-50 px-6 py-3">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-gray-400" />
            <span>
              顯示範圍：{format(start, 'yyyy/MM/dd')} - {format(end, 'yyyy/MM/dd')}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <AdjustmentsHorizontalIcon className="h-4 w-4 text-gray-400" />
            <div className="flex items-center space-x-1">
              {(Object.keys(statusStyles) as CalendarEventStatus[]).map((status) => (
                <label key={status} className="inline-flex items-center space-x-1">
                  <input
                    type="checkbox"
                    className="h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={filters.statuses.includes(status)}
                    onChange={() => handleToggleStatus(status)}
                  />
                  <span className="text-xs text-gray-600">{status}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">來源</span>
            {Object.keys(sourceLabels).map((source) => (
              <label key={source} className="inline-flex items-center space-x-1 text-xs">
                <input
                  type="checkbox"
                  className="h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={filters.sources.includes(source)}
                  onChange={() => handleToggleSource(source)}
                />
                <span>{sourceLabels[source]}</span>
              </label>
            ))}
          </div>
        </div>
      </section>

      <main className="flex-1 overflow-hidden">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">
            {error}
          </div>
        )}

        {currentView === 'month' && (
          <div className="grid h-full grid-cols-7 border-l border-t border-gray-200">
            {daysInRange.map((day) => {
              const isCurrentMonth = day.getMonth() === focusDate.getMonth()
              const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
              return (
                <div
                  key={day.toISOString()}
                  className={`flex min-h-[120px] flex-col border-b border-r border-gray-200 p-2 text-xs ${
                    isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                  } ${isToday ? 'ring-2 ring-primary-500 ring-inset' : ''}`}
                >
                  <div className="flex items-center justify-between text-gray-500">
                    <span>{format(day, 'd')}</span>
                    {!isCurrentMonth && (
                      <span className="text-[10px] text-gray-400">
                        {format(day, 'MMM', { locale: zhTW })}
                      </span>
                    )}
                  </div>
                  {renderEvents(day)}
                </div>
              )
            })}
          </div>
        )}

        {currentView === 'week' && (
          <div className="grid h-full grid-cols-7 border-l border-t border-gray-200">
            {daysInRange.map((day) => (
              <div
                key={day.toISOString()}
                className="flex min-h-[160px] flex-col border-b border-r border-gray-200 p-3"
              >
                <div className="text-sm font-medium text-gray-600">
                  {format(day, 'EEE dd', { locale: zhTW })}
                </div>
                {renderEvents(day)}
              </div>
            ))}
          </div>
        )}

        {currentView === 'day' && (
          <div className="h-full overflow-y-auto p-6">
            <h3 className="text-base font-semibold text-gray-700 mb-4">
              {format(focusDate, 'yyyy年MM月dd日 EEEE', { locale: zhTW })}
            </h3>
            <div className="space-y-3">
              {renderEvents(focusDate) || (
                <div className="rounded-lg border border-dashed border-gray-200 py-12 text-center text-sm text-gray-500">
                  此日期暫無事件
                </div>
              )}
            </div>
          </div>
        )}

        {loading && (
          <div className="absolute bottom-4 right-4 inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm shadow">
            <span className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
            正在載入事件...
          </div>
        )}
      </main>

      {selectedEvent && (
        <EventDetailDrawer
          event={selectedEvent}
          onClose={() => setSelectedEventId(null)}
        />
      )}
    </div>
  )
}

const EventDetailDrawer = ({
  event,
  onClose,
}: {
  event: CalendarEventItem
  onClose: () => void
}) => {
  const start = new Date(event.startTime)
  const end = event.endTime ? new Date(event.endTime) : null

  const timeLabel = end
    ? `${format(start, 'yyyy/MM/dd HH:mm')} - ${format(end, 'yyyy/MM/dd HH:mm')}`
    : `${format(start, 'yyyy/MM/dd HH:mm')}`

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-gray-900/20" onClick={onClose} />
      <aside className="relative h-full w-full max-w-md bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">事件詳情</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4 text-sm text-gray-700">
          <div>
            <div className="text-xs font-medium text-gray-500">標題</div>
            <div className="text-base font-semibold text-gray-900">{event.title}</div>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-500">時間</div>
            <div>{timeLabel}</div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="font-medium text-gray-500">狀態</div>
              <div className={`mt-1 inline-flex items-center rounded px-2 py-1 ${statusStyles[event.status]}`}>
                {event.status}
              </div>
            </div>
            <div>
              <div className="font-medium text-gray-500">來源</div>
              <div className="mt-1 inline-flex items-center rounded bg-gray-100 px-2 py-1 text-gray-700">
                {sourceLabels[event.source] || event.source}
              </div>
            </div>
          </div>

          {event.projectName && (
            <div>
              <div className="text-xs font-medium text-gray-500">所屬專案</div>
              <div>{event.projectName}</div>
            </div>
          )}

          {event.description && (
            <div>
              <div className="text-xs font-medium text-gray-500">描述</div>
              <p className="mt-1 whitespace-pre-wrap text-gray-600">{event.description}</p>
            </div>
          )}

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
            <div className="flex justify-between">
              <span>表單實例</span>
              <span>{event.formInstanceId ?? '—'}</span>
            </div>
            <div className="mt-1 flex justify-between">
              <span>排程規則</span>
              <span>{event.scheduleRuleId ?? '—'}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  )
}

export default CalendarSchedulerPage
